import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { processingApi } from '../services/api';
import { ProcessingFilters } from '../types/api';
import ProcessingTable from '../components/ProcessingTable';
import { useDebounce } from '../hooks/useDebounce';

export default function Processing() {
  const [filters, setFilters] = useState<ProcessingFilters>({
    page: 1,
    size: 20,
    search: '',
    status: '',
    source_type: '',
    user_id: undefined,
    sort_field: 'started_at',
    sort_direction: 'desc',
  });

  const debouncedSearch = useDebounce(filters.search || '', 500);

  const debouncedFilters = {
    ...filters,
    search: debouncedSearch,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['processingSessions', debouncedFilters],
    queryFn: () => processingApi.getProcessingSessions(debouncedFilters),
    keepPreviousData: true,
  });

  const { data: processingStats } = useQuery({
    queryKey: ['processingStats'],
    queryFn: processingApi.getProcessingStats,
    refetchInterval: 10000, // Обновляем каждые 10 секунд для активных сессий
  });

  const handleFilterChange = (newFilters: Partial<ProcessingFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      size: 20,
      search: '',
      status: '',
      source_type: '',
      user_id: undefined,
      sort_field: 'started_at',
      sort_direction: 'desc',
    });
  };

  const activeFiltersCount = [
    filters.search,
    filters.status,
    filters.source_type,
    filters.user_id,
  ].filter(Boolean).length;

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки сессий: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Сессии обработки
        </Typography>
        
        {processingStats && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              icon={<PlayArrowIcon />}
              label={`Активные: ${processingStats.active_sessions}`} 
              color="warning" 
              variant="outlined" 
            />
            <Chip 
              icon={<CheckCircleIcon />}
              label={`Успешные: ${processingStats.successful_sessions}`} 
              color="success" 
              variant="outlined" 
            />
            <Chip 
              icon={<ErrorIcon />}
              label={`Ошибки: ${processingStats.failed_sessions}`} 
              color="error" 
              variant="outlined" 
            />
          </Box>
        )}
      </Box>

      {/* Статистика */}
      {processingStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <PlayArrowIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight={600} color="warning.main">
                  {processingStats.active_sessions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Активные сессии
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight={600} color="success.main">
                  {processingStats.successful_sessions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Успешные
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight={600} color="error.main">
                  {processingStats.failed_sessions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  С ошибками
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ScheduleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" fontWeight={600}>
                  {processingStats.total_sessions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Всего сессий
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {processingStats.total_audio_minutes && (
            <Grid item xs={12} sm={6} md={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Обработано аудио
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {processingStats.total_audio_minutes.toFixed(1)} минут
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Поиск и фильтры
              {activeFiltersCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeFiltersCount} активных`}
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            {activeFiltersCount > 0 && (
              <Chip
                label="Очистить фильтры"
                variant="outlined"
                size="small"
                onDelete={clearFilters}
                deleteIcon={<ClearIcon />}
                onClick={clearFilters}
              />
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Поиск по Session ID или источнику"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange({ search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Введите Session ID или URL"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Статус"
                  onChange={(e) => handleFilterChange({ status: e.target.value || '' })}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="success">Успешно</MenuItem>
                  <MenuItem value="download_failed">Ошибка загрузки</MenuItem>
                  <MenuItem value="processing_failed">Ошибка обработки</MenuItem>
                  <MenuItem value="pending">Ожидание</MenuItem>
                  <MenuItem value="processing">В обработке</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Источник</InputLabel>
                <Select
                  value={filters.source_type || ''}
                  label="Источник"
                  onChange={(e) => handleFilterChange({ source_type: e.target.value || '' })}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                  <MenuItem value="telegram">Telegram</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="User ID"
                type="number"
                value={filters.user_id || ''}
                onChange={(e) => handleFilterChange({ 
                  user_id: e.target.value ? parseInt(e.target.value) : undefined 
                })}
                placeholder="ID пользователя"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Таблица */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading && !data ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ProcessingTable
              sessions={data?.items || []}
              total={data?.total || 0}
              loading={isLoading}
              filters={filters}
              onFiltersChange={setFilters}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}